import { useEffect, useId, useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { ApiClientError } from '../api/client';
import { api } from '../api/endpoints';
import { messageFor } from '../api/messages';
import { useAuth } from '../auth/AuthContext';
import { Logo, safeNext } from '../components/Layout';
import { clearPendingNext, pendingNext, rememberNext } from '../lib/pendingTarget';
import { useFragmentToken } from '../hooks/useFragmentToken';
import { Alert, Button, Card, Checkbox, TextField } from '../components/ui';

type AuthAsideVariant = 'login' | 'register' | 'account';

/** Inhalt der Markenspalte je Seite: kurz, sachlich, nur Funktionen, die es tatsächlich gibt. */
const ASIDE_CONTENT: Readonly<
  Record<AuthAsideVariant, { title: string; points: readonly string[] }>
> = {
  login: {
    title: 'Schön, dass Sie wieder da sind',
    points: [
      'Basare, Verkäuferanmeldungen und Helfereinsätze an einem Ort',
      'Die Kasse arbeitet auch bei schwachem WLAN weiter',
      'Ihre Daten liegen bei einem Anbieter in Deutschland',
    ],
  },
  register: {
    title: 'In wenigen Minuten startklar',
    points: [
      'Kostenlos für kleine Basare bis 100 Teile',
      'Ein Konto für Veranstalter, Verkäufer und Helfer',
      'Läuft im Browser, ohne Installation',
    ],
  },
  account: {
    title: 'Ihr Konto bleibt geschützt',
    points: [
      'Links aus unseren E-Mails gelten nur kurze Zeit und nur einmal',
      'Nach einer Passwortänderung endet die Anmeldung auf allen Geräten',
      'Ihre Daten liegen bei einem Anbieter in Deutschland',
    ],
  },
};

/**
 * Rahmen der Anmelde- und Kontoseiten: links das Formular, daneben eine Markenspalte mit Nutzen-Punkten. Auf schmalen
 * Bildschirmen steht das Formular zuerst, die Markenspalte darunter (gleiche Reihenfolge im Quelltext und auf dem
 * Bildschirm, damit Tastatur und Screenreader nicht springen).
 */
function AuthShell({
  title,
  lead,
  aside = 'account',
  children,
}: {
  title: string;
  lead?: ReactNode | undefined;
  aside?: AuthAsideVariant;
  children: ReactNode;
}) {
  const asideId = useId();
  const content = ASIDE_CONTENT[aside];
  return (
    <div className="auth-shell">
      <div className="auth-shell__main stack stack--lg">
        <div className="stack auth-shell__head">
          <h1>{title}</h1>
          {lead !== undefined && <p className="muted">{lead}</p>}
        </div>
        {children}
      </div>
      <aside className="auth-aside" aria-labelledby={asideId}>
        <div className="auth-aside__brand">
          <span className="auth-aside__logo">
            <Logo size={40} />
          </span>
          <span>
            <strong>BARSO</strong>
            <br />
            Nummernbasare einfach organisieren
          </span>
        </div>
        <h2 id={asideId}>{content.title}</h2>
        <ul className="check-list">
          {content.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        <p className="auth-aside__more">
          <Link to="/funktionen">Alle Funktionen ansehen</Link>
        </p>
      </aside>
    </div>
  );
}

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Ohne Ziel im Link (z. B. nach dem Öffnen der Bestätigungs-Mail in einem neuen Tab) gilt das bei der Registrierung gemerkte
  const [stored] = useState(() => pendingNext());
  const queryNext = safeNext(params.get('next'));
  const next = queryNext !== '/' ? queryNext : (stored ?? '/');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);

  // Das gemerkte Ziel ist verbraucht, sobald die Person angemeldet ist
  useEffect(() => {
    if (status === 'authenticated') clearPendingNext();
  }, [status]);

  if (status === 'authenticated') return <Navigate to={next} replace />;

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    try {
      await login(email, password, remember, needsCode ? code.trim() : undefined);
      clearPendingNext();
      void navigate(next, { replace: true });
    } catch (caught) {
      // Zweiter Schritt: Das Passwort stimmt, jetzt fehlt der Code der Authenticator-App
      if (caught instanceof ApiClientError && caught.code === 'TOTP_REQUIRED') {
        setNeedsCode(true);
        setBusy(false);
        return;
      }
      setUnverified(caught instanceof ApiClientError && caught.code === 'EMAIL_NOT_VERIFIED');
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    try {
      await api.resendVerification(email);
      setResent(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Anmelden"
      aside="login"
      lead="Mit Ihrem BARSO-Konto verwalten Sie Basare, Ihre Anmeldungen als Verkäufer und Ihre Einsätze als Helfer."
    >
      <Card>
        <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
          {error !== null && <Alert kind="error">{error}</Alert>}
          {resent && (
            <Alert kind="success">
              Wir haben Ihnen die E-Mail erneut geschickt. Bitte prüfen Sie Ihr Postfach.
            </Alert>
          )}
          <TextField
            label="E-Mail-Adresse"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <TextField
            label="Passwort"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          {needsCode && (
            <>
              <Alert kind="info">
                Ihr Konto ist mit einem zweiten Faktor geschützt. Bitte geben Sie den Code aus Ihrer
                Authenticator-App ein, oder einen Wiederherstellungscode.
              </Alert>
              <TextField
                label="Code"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
              />
            </>
          )}
          <Checkbox
            label="Angemeldet bleiben"
            checked={remember}
            onChange={(event) => {
              setRemember(event.target.checked);
            }}
          />
          <Button type="submit" busy={busy}>
            Anmelden
          </Button>
          {unverified && (
            <Button icon="mail" variant="secondary" onClick={() => void resend()} disabled={busy}>
              Bestätigungs-E-Mail erneut senden
            </Button>
          )}
        </form>
      </Card>
      <div className="auth-links">
        <p>
          <Link to="/passwort-vergessen">Passwort vergessen?</Link>
        </p>
        <p>
          Noch kein Konto?{' '}
          <Link to={`/registrieren?next=${encodeURIComponent(next)}`}>Jetzt registrieren</Link>
        </p>
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [accepted, setAccepted] = useState(false);
  const [age16, setAge16] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    if (!accepted) {
      setError(
        'Bitte akzeptieren Sie die Nutzungsbedingungen und nehmen Sie die Datenschutzerklärung zur Kenntnis.',
      );
      return;
    }
    if (!age16) {
      setError('Ein Konto können Sie ab 16 Jahren anlegen. Bitte bestätigen Sie Ihr Alter.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.register({ ...form, acceptTerms: true, confirmAge16: true });
      // Das Ziel überlebt die Bestätigung der Adresse (Mail-Link meist in einem neuen Tab)
      rememberNext(next);
      setDone(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthShell title="Bitte prüfen Sie Ihr Postfach" aside="register">
        <Alert kind="success">
          Wir haben eine E-Mail an <strong>{form.email}</strong> geschickt. Mit dem Link darin
          bestätigen Sie Ihre Adresse. Danach können Sie sich anmelden.
        </Alert>
        <p className="muted">
          Nichts angekommen? Schauen Sie auch im Spam-Ordner nach. Falls es für diese Adresse schon
          ein Konto gibt, haben wir Ihnen einen Hinweis dazu geschickt.
        </p>
        <p className="auth-links">
          <Link to={`/anmelden?next=${encodeURIComponent(next)}`}>Zur Anmeldung</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Konto anlegen"
      aside="register"
      lead="Ein Konto genügt, um einen Basar zu veranstalten, sich als Verkäufer anzumelden oder als Helfer mitzumachen."
    >
      <Card>
        <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
          {error !== null && <Alert kind="error">{error}</Alert>}
          <TextField
            label="Vorname"
            autoComplete="given-name"
            required
            value={form.firstName}
            onChange={(event) => {
              set('firstName')(event.target.value);
            }}
          />
          <TextField
            label="Nachname"
            autoComplete="family-name"
            required
            value={form.lastName}
            onChange={(event) => {
              set('lastName')(event.target.value);
            }}
          />
          <TextField
            label="E-Mail-Adresse"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => {
              set('email')(event.target.value);
            }}
          />
          <TextField
            label="Passwort"
            type="password"
            autoComplete="new-password"
            hint="Mindestens 10 Zeichen. Ein langer Satz ist ideal."
            required
            value={form.password}
            onChange={(event) => {
              set('password')(event.target.value);
            }}
          />
          <Checkbox
            label={
              <>
                Ich akzeptiere die{' '}
                <Link to="/nutzungsbedingungen" target="_blank">
                  Nutzungsbedingungen
                </Link>{' '}
                und habe die{' '}
                <Link to="/datenschutz" target="_blank">
                  Datenschutzerklärung
                </Link>{' '}
                zur Kenntnis genommen.
              </>
            }
            checked={accepted}
            onChange={(event) => {
              setAccepted(event.target.checked);
            }}
          />
          <Checkbox
            label="Ich bin mindestens 16 Jahre alt."
            checked={age16}
            onChange={(event) => {
              setAge16(event.target.checked);
            }}
          />
          <Button icon="plus" type="submit" busy={busy}>
            Konto anlegen
          </Button>
        </form>
      </Card>
      <p className="auth-links">
        Schon registriert? <Link to={`/anmelden?next=${encodeURIComponent(next)}`}>Anmelden</Link>
      </p>
    </AuthShell>
  );
}

type Verification = 'form' | 'working' | 'done';

/**
 * Bestätigung nur per Knopfdruck UND mit dem bei der Registrierung gewählten Passwort: Automatische Link-Prüfer
 * und versehentliche Klicks können ein von Fremden angelegtes Konto so nicht aktivieren.
 */
export function VerifyEmailPage() {
  const token = useFragmentToken();
  const [target] = useState(() => pendingNext());
  const [password, setPassword] = useState('');
  const [state, setState] = useState<Verification>('form');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    if (token === null) return;
    setState('working');
    setError(null);
    try {
      await api.verifyEmail(token, password);
      setState('done');
    } catch (caught) {
      setError(messageFor(caught));
      setState('form');
    }
  }

  if (token === null) {
    return (
      <AuthShell title="E-Mail-Adresse bestätigen">
        <Alert kind="error">
          Der Link ist unvollständig. Bitte öffnen Sie ihn direkt aus der E-Mail.
        </Alert>
        <p className="auth-links">
          <Link to="/anmelden">Zur Anmeldung</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="E-Mail-Adresse bestätigen">
      {state === 'done' ? (
        <>
          <Alert kind="success">Vielen Dank, Ihre E-Mail-Adresse ist bestätigt.</Alert>
          <Link
            className="btn"
            to={target === null ? '/anmelden' : `/anmelden?next=${encodeURIComponent(target)}`}
          >
            Jetzt anmelden
          </Link>
          {target !== null && (
            <p className="muted">Nach der Anmeldung geht es dort weiter, wo Sie gestartet sind.</p>
          )}
        </>
      ) : (
        <Card>
          <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
            <p>
              Geben Sie zur Bestätigung das Passwort ein, das Sie bei der Registrierung gewählt
              haben. Haben Sie sich nicht registriert, schließen Sie diese Seite einfach.
            </p>
            {error !== null && <Alert kind="error">{error}</Alert>}
            <TextField
              label="Ihr Passwort"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <Button type="submit" busy={state === 'working'}>
              Adresse bestätigen
            </Button>
          </form>
        </Card>
      )}
      <p className="muted auth-links">
        Passwort vergessen? <Link to="/passwort-vergessen">Neues Passwort festlegen</Link> (das
        bestätigt Ihre Adresse ebenfalls).
      </p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Passwort vergessen"
      lead={
        sent
          ? undefined
          : 'Geben Sie die E-Mail-Adresse Ihres Kontos ein. Wir schicken Ihnen einen Link, mit dem Sie ein neues Passwort wählen.'
      }
    >
      {sent ? (
        <Alert kind="success">
          Falls es zu dieser Adresse ein Konto gibt, haben wir Ihnen eine E-Mail mit einem Link
          geschickt. Der Link ist 60 Minuten gültig.
        </Alert>
      ) : (
        <Card>
          <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
            {error !== null && <Alert kind="error">{error}</Alert>}
            <TextField
              label="E-Mail-Adresse"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
            <Button icon="mail" type="submit" busy={busy}>
              Link anfordern
            </Button>
          </form>
        </Card>
      )}
      <p className="auth-links">
        <Link to="/anmelden">Zurück zur Anmeldung</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const token = useFragmentToken();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    if (token === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setBusy(false);
    }
  }

  if (token === null) {
    return (
      <AuthShell title="Neues Passwort">
        <Alert kind="error">Der Link ist unvollständig. Bitte fordern Sie einen neuen an.</Alert>
        <p className="auth-links">
          <Link to="/passwort-vergessen">Neuen Link anfordern</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Neues Passwort festlegen"
      lead={
        done
          ? undefined
          : 'Nach dem Speichern endet die Anmeldung auf allen Geräten. Danach melden Sie sich mit dem neuen Passwort wieder an.'
      }
    >
      {done ? (
        <>
          <Alert kind="success">Ihr Passwort wurde geändert. Sie wurden überall abgemeldet.</Alert>
          <Link className="btn" to="/anmelden">
            Jetzt anmelden
          </Link>
        </>
      ) : (
        <Card>
          <form className="stack" onSubmit={(event) => void submit(event)} noValidate>
            {error !== null && <Alert kind="error">{error}</Alert>}
            <TextField
              label="Neues Passwort"
              type="password"
              autoComplete="new-password"
              hint="Mindestens 10 Zeichen."
              required
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <Button icon="save" type="submit" busy={busy}>
              Passwort speichern
            </Button>
          </form>
        </Card>
      )}
    </AuthShell>
  );
}
